import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCaret from '@tiptap/extension-collaboration-caret';
import { caretRender, caretSelectionRender } from '@/lib/sync/collaboration/caret-render';
import * as Y from 'yjs';
import type { Awareness } from 'y-protocols/awareness';
import { useEntityYjs } from '@/lib/sync/use-entity-yjs';
import type { YjsRoomKind } from '@/lib/sync/yjs-registry';
import { collabColorFromId } from '@/utils/collab-color';
import type { DiffRange } from '@/lib/diff/types';
import { EditorSkeleton } from './editor-skeleton';
import { collaborativeViewerExtensions } from './read-only-extensions';
import { useDiffHighlight } from '../plugins/diff-highlight';
import styles from './rich-text-viewer.module.css';
import { Flex, Skeleton } from '@nula/core-ui';

type SharedProps = {
  onStats?: (s: { wordCount: number; markdown?: string }) => void;
  diffRanges?: DiffRange[];
  username?: string;
  cursorColor?: string;
};

/**
 * Core read-only Tiptap viewer.
 *
 * Takes a RESOLVED Y.Doc + optional Awareness — no sync hook. Used by:
 *   - Share-link viewer (injected yDoc/awareness, no SyncSystemProvider)
 *   - Snapshot viewer (injected yDoc + fake awareness, no SyncSystemProvider)
 *   - EntityDocumentViewer (wraps this with resolved doc/awareness from useEntityYjs)
 *
 * Mirrors the CanvasViewer pattern — the outer "preview" wrapper owns the hook,
 * the inner viewer only takes resolved values.
 */
export interface RichTextViewerProps extends SharedProps {
  doc: Y.Doc;
  awareness?: Awareness | null;
  /**
   * Optional entity id used only for cursor coloring when awareness is present
   * and no explicit cursorColor is provided.
   */
  entityId?: string;
  /** The Yjs fragment (Collaboration `field`) this editor binds to. Default 'prosemirror'. */
  fragmentName?: string;
}

export function RichTextViewer({
  doc,
  awareness,
  entityId,
  onStats,
  diffRanges,
  username,
  cursorColor,
  fragmentName,
}: RichTextViewerProps) {
  const editor = useEditor(
    {
      editable: false,
      immediatelyRender: false,
      // Carry the same typography root the editable editor uses so the ~95
      // `.nula-editor-root <el>` rich-content rules in globals.css (headings,
      // lists, code blocks, tables, callouts) apply to the read-only view —
      // without this, shared/snapshot/diff docs render unformatted.
      editorProps: { attributes: { class: 'nula-editor-root' } },
      extensions: [
        ...collaborativeViewerExtensions(),
        Collaboration.configure({ document: doc, field: fragmentName ?? 'prosemirror' }),
        ...(awareness
          ? [
              CollaborationCaret.configure({
                provider: { awareness } as never,
                user: {
                  name: username ?? 'Anonymous',
                  color: cursorColor ?? collabColorFromId(entityId ?? 'anonymous'),
                },
                render: caretRender,
                selectionRender: caretSelectionRender,
              }),
            ]
          : []),
      ],
    },
    [doc, awareness],
  );

  // Emit word-count stats after a brief delay (let ProseMirror hydrate)
  useEffect(() => {
    if (!editor || !onStats) return;
    const timer = setTimeout(() => {
      const text = editor.getText();
      const words = text.trim().split(/\s+/).filter(Boolean);
      onStats({ wordCount: words.length });
    }, 300);
    return () => clearTimeout(timer);
  }, [editor, onStats]);

  // Apply diff highlighting (CSS Custom Highlight API)
  useDiffHighlight(editor ?? null, diffRanges ?? []);

  return (
    <EditorContent
      editor={editor}
      className={styles.root}
      data-testid="viewer-content"
    />
  );
}

// ── Entity-mode wrapper (mirrors CanvasViewerPreview) ─────────────────────────

/**
 * Read-only viewer that subscribes to the entity's Yjs room via useEntityYjs.
 *
 * Use this wherever a SyncSystemProvider ancestor is guaranteed — e.g. the
 * main app router and diff-view. NEVER use this from share-link-viewer or
 * snapshot-viewer (they have no SyncSystemProvider).
 *
 * Registered as the DocumentViewerProvider renderer in router.tsx so that
 * entity-preview-card and other lib/ consumers can render document previews
 * without violating the components → features layer boundary.
 */
export interface EntityDocumentViewerProps extends SharedProps {
  entityId: string;
  /**
   * Which Yjs room to subscribe to. Defaults to `'document'` (legacy callers).
   */
  entityKindHint?: YjsRoomKind;
  /**
   * The Yjs fragment (Collaboration `field`) to bind. Defaults to undefined →
   * `RichTextViewer` falls back to `'prosemirror'` (legacy document content).
   */
  fragmentName?: string;
}

export function EntityDocumentViewer({
  entityId,
  entityKindHint,
  fragmentName,
  onStats,
  diffRanges,
  username,
  cursorColor,
}: EntityDocumentViewerProps) {
  const { doc, awareness } = useEntityYjs(entityId, entityKindHint ?? 'document');

  if (!doc) {
    return <EditorSkeleton />;
  }

  return (
    <RichTextViewer
      doc={doc as Y.Doc}
      awareness={awareness as Awareness | null}
      entityId={entityId}
      fragmentName={fragmentName}
      onStats={onStats}
      diffRanges={diffRanges}
      username={username}
      cursorColor={cursorColor}
    />
  );
}

// ── ReadOnlyEditorSkeleton ─────────────────────────────────────────────────────

/**
 * Skeleton for the read-only viewer — used by share-link-viewer and
 * other surfaces that show a loading state before the doc is ready.
 */
export function ReadOnlyEditorSkeleton() {
  return (
    <Flex vertical gap="lg" className={styles.skeleton}>
      <Skeleton
        active
        paragraph={{ rows: 1 }}
        title={false}
        className={styles.skeletonTitle}
        style={{ '--readonly-editor-skeleton-title-width': '300px' } as React.CSSProperties}
      />
      <Skeleton active paragraph={{ rows: 5 }} title={false} />
      <Skeleton active paragraph={{ rows: 4 }} title={false} />
      <Skeleton active paragraph={{ rows: 3 }} title={false} />
    </Flex>
  );
}
